import { useState } from "react";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { useAtom, useAtomValue } from "jotai";
import { useTimeout, useWindowSize } from "usehooks-ts";

import { VotingActiveSession } from "./voting-active-session";
import { ShowSingleSession, VotingHistory } from "./voting-history";
import { TabBar } from "../../../ui-components/design-system/tab-bar/tab-bar-better";
import style from "./voting-panel.module.css";
import sidepanel from "../../side-panel/side-panel.module.css";

import type { VotingSession, LegacyVotingSession } from "shared/datamodel/schemas/voting-session";
import {
  checkParticipation,
  Participation,
  timeLeftForSession,
  useActiveVotingSession,
  votingAtom,
} from "../voting-common";
import { VotingIcon } from "frontend/app-header/right-header/icons";
import { CloseIcon } from "frontend/icons/close";
import { Toolbar } from "./components/panel-toolbar";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { sidePanelType, SidepanelType } from "state-atoms";
import cn from "classnames";

type OldAndNew = LegacyVotingSession | VotingSession;

const enum Tab {
  NewSession = 0,
  Results = 1,
}

interface VotingMainPanelProps {
  syncService: SyncService<RW> | null;
  onDismiss: () => void;
}

export function VotingMainPanel({ syncService, onDismiss }: VotingMainPanelProps) {
  const [activeTab, setActiveTab] = useState<Tab>(Tab.NewSession);
  const { height: windowHeight } = useWindowSize();
  const tabBarContainerHeight = windowHeight < 650 ? { height: 36 } : {};

  const [sessionInHistoryPanel, setSessionInHistoryPanel] = useState<OldAndNew | null>(null);
  const current = useActiveVotingSession(syncService);
  const state = useAtomValue(votingAtom);

  const sessionInProgress = current && (timeLeftForSession(current) ?? -1) >= 0;
  const participationStatus = checkParticipation(state, current);
  const isInSession = participationStatus === Participation.Joined || participationStatus === Participation.Done;
  const participating = sessionInProgress && isInSession;
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const [openedSidePanel, setOpenedSidePanel] = useAtom(sidePanelType);

  let timeout = null;

  const timeLeft = timeLeftForSession(current);
  if (participating && timeLeft) {
    timeout = timeLeft - 1;
  }

  useTimeout(() => {
    setSessionInHistoryPanel(current!);
  }, timeout); // when session is over, switch to history view and focus on session

  function renderMainScreen() {
    return (
      <>
        <div className={cn(style.mainScreenContainer, isCimReorderEnabled && style.mainScreenContainerWithBorder)}>
          <TabBar onTabChange={setActiveTab} customStyle={tabBarContainerHeight}>
            <span>New session</span>
            <span>Results</span>
          </TabBar>
        </div>
        {activeTab === Tab.NewSession && <VotingActiveSession syncService={syncService} />}
        {activeTab === Tab.Results && <VotingHistory syncService={syncService} setSession={setSessionInHistoryPanel} />}
      </>
    );
  }

  function renderSessionView() {
    if (!sessionInHistoryPanel) return null;
    return <ShowSingleSession syncService={syncService} session={sessionInHistoryPanel} />;
  }

  if (isCimReorderEnabled) {
    if (openedSidePanel !== SidepanelType.voting) {
      return null;
    }
    return (
      <div className={style.floatingPanel} data-floating-panel>
        <div className={style.floatingPanelHeader}>
          <div className={style.floatingPanelTitle}>
            <VotingIcon />
            <span>Voting</span>
          </div>
          <div
            className={style.floatingPanelClose}
            onClick={() => {
              onDismiss();
              setOpenedSidePanel(SidepanelType.none);
            }}
          >
            <CloseIcon />
          </div>
        </div>
        <div className={style.floatingPanelContent}>
          {sessionInHistoryPanel != null && (
            <Toolbar
              onDismiss={() => setSessionInHistoryPanel(null)}
              showBackButton={true}
              onBackButtonClick={() => setSessionInHistoryPanel(null)}
            />
          )}
          {sessionInHistoryPanel == null ? renderMainScreen() : renderSessionView()}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={sidepanel.container}
        style={{
          paddingTop: document.getElementById("app-header-container")?.clientHeight || 100,
        }}
      >
        <Toolbar
          onDismiss={onDismiss}
          showBackButton={sessionInHistoryPanel != null}
          onBackButtonClick={() => setSessionInHistoryPanel(null)}
        />
        {sessionInHistoryPanel == null ? renderMainScreen() : renderSessionView()}
      </div>
    </>
  );
}
